import * as Sentry from '@sentry/nextjs';

type LogSeverityLevel = 'warn' | 'info' | 'error';
type LogData = Record<string, unknown>;

class Logger {
  private summarizeForConsole(value: unknown): unknown {
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        if (value.length > 3) {
          return `[Array with ${value.length} items]`;
        }
        return value.map((item) => this.summarizeForConsole(item));
      }

      const keys = Object.keys(value);
      if (keys.length > 5) {
        return `{Object with ${keys.length} properties: ${keys.slice(0, 3).join(', ')}...}`;
      }

      // For smaller objects, summarize their values too
      const summarized: Record<string, unknown> = {};
      Object.keys(value).forEach((key) => {
        summarized[key] = this.summarizeForConsole((value as Record<string, unknown>)[key]);
      });
      return summarized;
    }

    if (typeof value === 'string' && value.length > 100) {
      return `${value.substring(0, 100)}... (${value.length} chars)`;
    }

    return value;
  }

  private log(level: LogSeverityLevel, message: string, metadata?: LogData): void {
    // Prepare metadata for Sentry
    const logData: Record<string, unknown> = {};

    if (metadata) {
      Object.assign(logData, metadata);
    }

    // Console logging with summarized metadata
    const summarizedLogData = { ...logData };
    Object.keys(summarizedLogData).forEach((key) => {
      summarizedLogData[key] = this.summarizeForConsole(summarizedLogData[key]);
    });

    if (Object.keys(summarizedLogData).length > 0) {
      if (level === 'error') {
        console.error(`[${level.toUpperCase()}] ${message}`, summarizedLogData);
      } else if (level === 'warn') {
        console.warn(`[${level.toUpperCase()}] ${message}`, summarizedLogData);
      } else {
        console.log(`[${level.toUpperCase()}] ${message}`, summarizedLogData);
      }
    } else {
      if (level === 'error') {
        console.error(`[${level.toUpperCase()}] ${message}`);
      } else if (level === 'warn') {
        console.warn(`[${level.toUpperCase()}] ${message}`);
      } else {
        console.log(`[${level.toUpperCase()}] ${message}`);
      }
    }

    // Sentry logging
    let sentryMethod;
    if (level === 'error') {
      sentryMethod = Sentry.logger.error;
    } else if (level === 'warn') {
      sentryMethod = Sentry.logger.warn;
    } else {
      sentryMethod = Sentry.logger.info;
    }

    if (Object.keys(logData).length > 0) {
      sentryMethod(message, logData);
    } else {
      sentryMethod(message);
    }
  }

  error(message: string, metadata?: LogData): void {
    this.log('error', message, metadata);
  }

  warn(message: string, metadata?: LogData): void {
    this.log('warn', message, metadata);
  }

  info(message: string, metadata?: LogData): void {
    this.log('info', message, metadata);
  }
}

const logger = new Logger();
export default logger;
